import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { mutateWithLocation, nowTimePretty } from '../helpers';

async function fetchProjectLine({ ProjectLineId, workType }) {
    const response = await axios.get(`api/ProjectLine/GetProjectLineById/${ProjectLineId}?workType=${workType}`);
    if (response.status !== 200) throw new Error(`fetchProjectLine: ${response.status}`);
    return response.data;
};

async function postStartProjectLine({ ProjectLineId, Location, WorkerId, TotalInGroup }) {
    const response = await axios.post('api/ProjectLine/StartProjectLine', { ProjectLineId, DateCheckedIn: new Date(), Location, WorkerId, TotalInGroup });
    if (response.status !== 200) throw new Error(`startProjectLine: ${response.status}`);
    return response.data;
};

async function postStopProjectLine({ ProjectLineId, Location, WorkerId, TotalInGroup }) {
    const response = await axios.post('api/ProjectLine/StopProjectLine', { ProjectLineId, DateCheckedOut: new Date(), Location, WorkerId, TotalInGroup });
    if (response.status !== 200) throw new Error(`stopProjectLine: ${response.status}`);
    return response.data;
};

async function postSkipProjectLine({ ProjectLineId, Location, WorkerId, TotalInGroup, skip }) {
    const response = await axios.post('api/ProjectLine/SkipProjectLine', { ProjectLineId, DateSkipped: new Date(), skip, Location, WorkerId, TotalInGroup });
    if (response.status !== 200) throw new Error(`skipProjectLine: ${response.status}`);
    return response.data;
};

async function updateProjectLineIssue({ ProjectLineId, Issue, TotalInGroup }) {
    const response = await axios.post('api/ProjectLine/UpdateProjectLineIssue', {
        ProjectLineId, Issue, TotalInGroup
    });
    if (response.status !== 200) {
        throw new Error(`updateIssue: ${response.status}`);
    }
    return response.data;
}

const useProjectLineClock = ({ projectLineId, workType }) => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const [projectLineDetails, setProjectLineDetails] = useState({
        hasStartedWork: false,
        hasEndedWork: false,
        isWorkOver: false,
        startDateFormatted: null,
        endDateFormatted: null,
        totalTime: 0,
    });
    // Keep track of the POST request finished
    const [mutationSuccess, setMutationSuccess] = useState({
        start: false,
        stop: false,
        skip: false,
    });
    
    const showSnackbar = (message, variant) => {
        enqueueSnackbar(message, {
            variant: variant,
            autoHideDuration: 3500,
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            preventDuplicate: true,
        });
    };

    // Queries
    const { data, error, isLoading, isSuccess, isError } = useQuery(
        ['projectLine', projectLineId, workType],
        () => fetchProjectLine({ ProjectLineId: projectLineId, workType }),
        {
            refetchOnWindowFocus: true,
            enabled: !!projectLineId && !!workType,
            retry: (failureCount, error) => error?.response?.status !== 400 && failureCount < 3,
            onError: (error) => {
                if (error?.response?.status !== 400) {
                    showSnackbar(`Error occurred while getting the work job`, "error");
                }
            },
        }
    );
    useEffect(() => {
        if (isSuccess && data?.projectLine) {
            const { checkIn, checkOut, skipped, totalTime } = data.projectLine;
            const hasStarted = !!checkIn;
            const hasEnded = !!checkOut;
            const isOver = (hasStarted && hasEnded) || skipped;
            setProjectLineDetails({
                hasStartedWork: hasStarted,
                hasEndedWork: hasEnded,
                isWorkOver: isOver,
                startDateFormatted: hasStarted ? new Date(checkIn).toLocaleString("de-GE") : null,
                endDateFormatted: hasEnded ? new Date(checkOut).toLocaleString("de-GE") : null,
                totalTime: totalTime ?? 0,
            });
        }
        if (mutationSuccess.start) {
            setProjectLineDetails(prev => ({
                ...prev,
                hasStartedWork: true,
                startDateFormatted: nowTimePretty()
            }));
        }
        if (mutationSuccess.stop) {
            setProjectLineDetails(prev => ({
                ...prev,
                hasEndedWork: true,
                endDateFormatted: nowTimePretty()
            }));
        }
        if (mutationSuccess.skip) {
            setProjectLineDetails(prev => ({
                ...prev,
                isWorkOver: true,
            }));
        }
    }, [isSuccess, data?.projectLine, mutationSuccess]);

    // Mutations
    const { mutate: startProjectLineMutation, isLoading: isLoadingStartProjectLine } = useMutation(
        postStartProjectLine,
        {
            onSuccess: () => {
                showSnackbar(
                    `${data?.projectLine?.project?.address?.name ? `Started work at ${data?.projectLine?.project?.address?.name} on ${nowTimePretty()}` : `Started work at ${nowTimePretty()}`}`,
                    "success"
                );
                queryClient.invalidateQueries(['projectLine', projectLineId, workType]);
            },
            onError: (error) => {
                showSnackbar("Error starting work", "error")
            },
        }
    );
    const { mutate: stopProjectLineMutation, isLoading: isLoadingStopProjectLine } = useMutation(
        postStopProjectLine,
        {
            onSuccess: () => {
                showSnackbar(
                    `${data?.projectLine?.project?.address?.name ? `Ended work at ${data?.projectLine?.project?.address?.name} on ${nowTimePretty()}` : `Ended work at ${nowTimePretty()}`}`,
                    "success"
                );
                queryClient.invalidateQueries(['projectLine', projectLineId, workType]);
            },
            onError: (error) => {
                showSnackbar("Error ending work", "error")
            },
        }
    );
    const { mutate: skipProjectLineMutation, isLoading: isLoadingSkipProjectLine } = useMutation(
        postSkipProjectLine,
        {
            onSuccess: () => {
                showSnackbar(
                    `${data?.projectLine?.project?.address?.name ? `Skipped work at ${data?.projectLine?.project?.address?.name} on ${nowTimePretty()}` : `Skipped work at ${nowTimePretty()}`}`,
                    "success"
                );
                queryClient.invalidateQueries(['projectLine', projectLineId, workType]);
            },
            onError: (error) => {
                showSnackbar("Error skipping work", "error")
            },
        }
    );
    const { mutate: updateIssueMutation, isLoading: isLoadingUpdateIssue } = useMutation(
        updateProjectLineIssue,
        {
            onSuccess: () => {
                showSnackbar("Work info updated", "success");
                queryClient.invalidateQueries(['projectLine', projectLineId, workType]);
            },
            onError: (error) => {
                showSnackbar("Error occured while updating the issue", "error")
            },
        }
    );

    const updateIssue = (issueData) => {
        console.log("🔥 Calling Update Issue Mutation", issueData);
        updateIssueMutation(issueData);
    };
    const startWork = (WorkerId, TotalInGroup) => {
        mutateWithLocation(startProjectLineMutation, { ProjectLineId: projectLineId, WorkerId, TotalInGroup })
            .then(() => { setMutationSuccess((prev) => ({ ...prev, start: true })); })
            .catch((message) => { showSnackbar(message, 'error'); });
    };
    const stopWork = (WorkerId, TotalInGroup) => {
        mutateWithLocation(stopProjectLineMutation, { ProjectLineId: projectLineId, WorkerId, TotalInGroup })
            .then(() => { setMutationSuccess((prev) => ({ ...prev, stop: true })); })
            .catch((message) => { showSnackbar(message, 'error'); });
    };
    const skipWork = (WorkerId, TotalInGroup) => {
        mutateWithLocation(skipProjectLineMutation, { ProjectLineId: projectLineId, WorkerId, TotalInGroup, skip: true })
            .then(() => { setMutationSuccess((prev) => ({ ...prev, skip: true })); })
            .catch((message) => { showSnackbar(message, 'error'); });
    };

    return {
        startWork,
        stopWork,
        skipWork,
        isLoading,
        isSuccess,
        isError,
        error,
        updateIssue,
        isLoadingUpdateIssue,
        isLoadingSkipProjectLine,
        isLoadingStartProjectLine,
        isLoadingStopProjectLine,
        projectLine: data?.projectLine,
        accountLocationDescription: data?.accountLocationDescription,
        relatedProjectLines: data?.relatedProjectLines,
        approvedByContact: data?.approvedByContact,
        projectLineDetails
    };
};

export default useProjectLineClock;
